// FLAGS: none
// Per-tab language memory in session storage: the prefix the app wrote in (a guess the profile replaces; a visitor's
// own stays unless a signed-in user's saved language differs), the profile hook's reload breaker and the signed-out
// pick. Any script can write there, so only real values come back; nothing throws.
import {
  getLanguagePathPrefix,
  prefixForLanguage,
} from "@src/utils/languagePathPrefix";

const KEY = "languagePrefixAddedByApp";

export const rememberAddedLanguagePrefix = (prefix: string): void => {
  try {
    sessionStorage.setItem(KEY, prefix);
  } catch {
    // Blocked storage: the prefix is then treated as the visitor's choice.
  }
};

export const getAddedLanguagePrefix = (): string | undefined => {
  try {
    const value = sessionStorage.getItem(KEY);
    return value !== null && getLanguagePathPrefix(`/${value}`) === value
      ? value
      : undefined;
  } catch {
    return undefined;
  }
};

export const forgetAddedLanguagePrefix = (): void => {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // Nothing was stored.
  }
};

const RELOAD_KEY = "languagePrefixReloadedFor";

// The profile code the profile hook last reloaded for in this tab: it never reloads twice for one code, so if the
// boot ever lands on a different prefix than the hook expected, the cost is one extra load, not a loop.
export const rememberProfileReload = (code: string): void => {
  try {
    sessionStorage.setItem(RELOAD_KEY, code);
  } catch {
    // Blocked storage: the hook's own guards still apply.
  }
};

export const hasReloadedForProfile = (code: string): boolean => {
  try {
    return sessionStorage.getItem(RELOAD_KEY) === code;
  } catch {
    // Unreadable: answer as if it had, so a reload never repeats unchecked.
    return true;
  }
};

export const forgetProfileReload = (): void => {
  try {
    sessionStorage.removeItem(RELOAD_KEY);
  } catch {
    // Nothing was stored.
  }
};

const PICKED_KEY = "languagePickedInTab";

// Read by the boot when no stored profile answers, so a signed-out pick outlives its URL.
export const rememberPickedLanguage = (code: string): void => {
  try {
    sessionStorage.setItem(PICKED_KEY, code);
  } catch {
    // Blocked storage: the pick then lasts only as long as its URL.
  }
};

export const getPickedLanguage = (): string | undefined => {
  try {
    const value = sessionStorage.getItem(PICKED_KEY);
    return value !== null && prefixForLanguage(value) !== undefined
      ? value
      : undefined;
  } catch {
    return undefined;
  }
};

// Signing in clears it (the saved language governs then), so it never outlasts a session for the next visitor.
export const forgetPickedLanguage = (): void => {
  try {
    sessionStorage.removeItem(PICKED_KEY);
  } catch {
    // Nothing was stored.
  }
};
